import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePortal } from './PortalContext';
import {
  buildWeekDates,
  buildWeekScheduleSegments,
  fmtHour,
  isMinuteBlocked,
  portalInquiryOverlapsSibling,
  pxToTime,
  SCHEDULE_LEEWAY_MIN,
  SCHEDULE_MERGE_GAP_MIN,
  toDateStr,
  timeToPx,
  WEEK_END_H,
  WEEK_HOUR_PX,
  WEEK_SCHED_HRS,
  WEEK_START_H,
  PORTAL_INQUIRY_DURATION_MIN
} from './portalWeekScheduleUtils';

function formatPortalTime(timeString) {
  if (!timeString) return '';
  const t = timeString.length <= 5 ? `${timeString}:00` : timeString;
  return new Date(`2000-01-01T${t}`).toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function mapServiceRowsToInquiries(rows) {
  return (rows || []).map((r) => ({
    id: r.id,
    issue: r.description,
    preferred_date: r.scheduled_date,
    preferred_time: r.scheduled_time,
    created_at: r.created_at,
    status: r.status,
    job_id: r.job_id,
    quote_id: r.quote_id,
    inspection_id: r.inspection_id
  }));
}

const PortalServiceRequest = () => {
  const [scheduleJobs, setScheduleJobs] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [issue, setIssue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [nowTick, setNowTick] = useState(() => new Date());
  /** While dragging a portal request to reschedule */
  const [dragGhost, setDragGhost] = useState(null);
  /** Hover preview on empty grid (snapped time) */
  const [hoverSlot, setHoverSlot] = useState(null);
  /** While pointer is down — slot follows mouse until release */
  const [slotPick, setSlotPick] = useState(null);

  const navigate = useNavigate();
  const {
    customer,
    isAuthenticated,
    fetchBusinessSchedule,
    fetchServiceRequests,
    submitServiceRequest,
    updateServiceRequest,
    logout
  } = usePortal();

  const refreshInquiries = useCallback(async () => {
    try {
      const rows = await fetchServiceRequests();
      setInquiries(mapServiceRowsToInquiries(rows));
    } catch {
      setInquiries([]);
    }
  }, [fetchServiceRequests]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }
    refreshInquiries();
  }, [isAuthenticated, navigate, refreshInquiries]);

  const loadSchedule = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!isAuthenticated || !customer) return;
    if (!silent) setLoading(true);
    try {
      const dates = buildWeekDates(weekOffset);
      const from = toDateStr(dates[0]);
      const to = toDateStr(dates[6]);
      const res = await fetchBusinessSchedule(from, to);
      setScheduleJobs(res?.jobs || []);
    } catch {
      setScheduleJobs([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isAuthenticated, customer, fetchBusinessSchedule, weekOffset]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const scheduleDates = useMemo(() => buildWeekDates(weekOffset), [weekOffset]);
  const dateStrSet = useMemo(() => new Set(scheduleDates.map(toDateStr)), [scheduleDates]);
  const todayStr = toDateStr(new Date());

  const { byDate, noTime, blockedRangesByDate } = useMemo(
    () => buildWeekScheduleSegments(scheduleJobs, inquiries, dateStrSet, WEEK_HOUR_PX),
    [scheduleJobs, inquiries, dateStrSet]
  );

  const totalSchedulePx = WEEK_SCHED_HRS.length * WEEK_HOUR_PX;

  const inquiriesRef = useRef(inquiries);
  const scheduleJobsRef = useRef(scheduleJobs);
  const dateStrSetRef = useRef(dateStrSet);
  const ignoreNextColumnClickRef = useRef(false);
  const blockedRangesByDateRef = useRef(blockedRangesByDate);
  const slotPickActiveRef = useRef(false);
  const slotPickRef = useRef(null);
  useEffect(() => {
    inquiriesRef.current = inquiries;
    scheduleJobsRef.current = scheduleJobs;
    dateStrSetRef.current = dateStrSet;
  }, [inquiries, scheduleJobs, dateStrSet]);
  useEffect(() => {
    blockedRangesByDateRef.current = blockedRangesByDate;
  }, [blockedRangesByDate]);

  useEffect(() => {
    setHoverSlot(null);
    setSlotPick(null);
  }, [weekOffset]);

  useEffect(() => {
    if (dragGhost) setHoverSlot(null);
  }, [dragGhost]);

  const handleInquiryDragStart = useCallback(
    (e, seg) => {
      if (!seg.draggableInquiryId) return;
      e.preventDefault();
      e.stopPropagation();
      const inquiryId = seg.draggableInquiryId;
      const blockHeightPx = Math.max(22, seg.height || WEEK_HOUR_PX - 4);
      setDragGhost({
        inquiryId,
        title: seg.title,
        x: e.clientX,
        y: e.clientY,
        blockHeightPx,
        dropPreview: null
      });

      const onMove = (ev) => {
        setDragGhost((g) => {
          if (!g) return null;
          const el = document.elementFromPoint(ev.clientX, ev.clientY);
          const col = el && el.closest ? el.closest('[data-portal-day]') : null;
          if (!col) {
            return { ...g, x: ev.clientX, y: ev.clientY, dropPreview: null };
          }
          const dateStr = col.getAttribute('data-portal-day');
          if (!dateStr) {
            return { ...g, x: ev.clientX, y: ev.clientY, dropPreview: null };
          }
          const rect = col.getBoundingClientRect();
          const y = ev.clientY - rect.top;
          if (y < 0 || y > totalSchedulePx) {
            return { ...g, x: ev.clientX, y: ev.clientY, dropPreview: null };
          }
          const timeStr = pxToTime(y, WEEK_HOUR_PX);
          const topPx = timeToPx(timeStr, WEEK_HOUR_PX);
          const heightPx = g.blockHeightPx;

          const tempInquiries = inquiriesRef.current.filter((i) => String(i.id) !== String(g.inquiryId));
          const { blockedRangesByDate: br } = buildWeekScheduleSegments(
            scheduleJobsRef.current,
            tempInquiries,
            dateStrSetRef.current,
            WEEK_HOUR_PX
          );
          const invalid =
            isMinuteBlocked(dateStr, timeStr, br) ||
            portalInquiryOverlapsSibling(inquiriesRef.current, dateStr, timeStr, g.inquiryId);

          return {
            ...g,
            x: ev.clientX,
            y: ev.clientY,
            dropPreview: { dateStr, timeStr, topPx, heightPx, invalid }
          };
        });
      };

      const onUp = async (ev) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        setDragGhost(null);
        ignoreNextColumnClickRef.current = true;
        window.setTimeout(() => {
          ignoreNextColumnClickRef.current = false;
        }, 350);

        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        const col = el && el.closest ? el.closest('[data-portal-day]') : null;
        if (!col) return;
        const dateStr = col.getAttribute('data-portal-day');
        if (!dateStr) return;
        const rect = col.getBoundingClientRect();
        const y = ev.clientY - rect.top;
        if (y < 0 || y > totalSchedulePx) return;
        const timeStr = pxToTime(y, WEEK_HOUR_PX);

        const tempInquiries = inquiriesRef.current.filter((i) => String(i.id) !== String(inquiryId));
        const { blockedRangesByDate: br } = buildWeekScheduleSegments(
          scheduleJobsRef.current,
          tempInquiries,
          dateStrSetRef.current,
          WEEK_HOUR_PX
        );
        if (isMinuteBlocked(dateStr, timeStr, br)) {
          setFormError(
            `That time is unavailable (includes ±${SCHEDULE_LEEWAY_MIN} min buffer around other bookings).`
          );
          return;
        }
        if (portalInquiryOverlapsSibling(inquiriesRef.current, dateStr, timeStr, inquiryId)) {
          setFormError('That time overlaps another of your requests. Choose a different slot.');
          return;
        }

        const timeNorm = timeStr.trim().length <= 5 ? `${timeStr.trim()}:00` : timeStr.trim();

        try {
          await updateServiceRequest(inquiryId, { preferred_date: dateStr, preferred_time: timeStr });
          setInquiries((prev) =>
            prev.map((i) =>
              String(i.id) === String(inquiryId)
                ? { ...i, preferred_date: dateStr, preferred_time: timeNorm }
                : i
            )
          );
          setFormError(null);
          void refreshInquiries();
          void loadSchedule({ silent: true });
        } catch (err) {
          setFormError(err.message || 'Could not move your request.');
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [refreshInquiries, loadSchedule, totalSchedulePx, updateServiceRequest]
  );

  const handleLogout = async () => {
    await logout();
  };

  /** Match portal inquiry duration on the grid (~30 min), not a full hour */
  const slotBoxHeightPx = Math.max(
    20,
    Math.round((PORTAL_INQUIRY_DURATION_MIN / 60) * WEEK_HOUR_PX)
  );

  const handleColumnMouseMove = useCallback(
    (dateStr, e) => {
      if (dragGhost) return;
      if (slotPickActiveRef.current) return;
      const under = document.elementFromPoint(e.clientX, e.clientY);
      if (under && under.closest && under.closest('[data-portal-event="1"]')) {
        setHoverSlot(null);
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      if (y < 0 || y > totalSchedulePx) {
        setHoverSlot(null);
        return;
      }
      const timeStr = pxToTime(y, WEEK_HOUR_PX);
      const br = blockedRangesByDateRef.current;
      const inv = inquiriesRef.current;
      const invalid =
        isMinuteBlocked(dateStr, timeStr, br) || portalInquiryOverlapsSibling(inv, dateStr, timeStr, null);
      if (invalid) {
        setHoverSlot(null);
        return;
      }
      setHoverSlot({ dateKey: dateStr, timeStr, invalid: false });
    },
    [dragGhost, totalSchedulePx]
  );

  const handleColumnMouseLeave = useCallback(() => {
    if (!slotPickActiveRef.current) setHoverSlot(null);
  }, []);

  const handleColumnPointerDown = useCallback(
    (dateStr, e) => {
      if (ignoreNextColumnClickRef.current) return;
      if (e.button !== 0) return;
      if (e.target.closest && e.target.closest('[data-portal-event="1"]')) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      if (y < 0 || y > totalSchedulePx) return;
      const timeStr = pxToTime(y, WEEK_HOUR_PX);
      const br = blockedRangesByDateRef.current;
      const inv = inquiriesRef.current;
      if (isMinuteBlocked(dateStr, timeStr, br)) {
        setFormError(
          `That time is unavailable — it overlaps a busy slot (includes ±${SCHEDULE_LEEWAY_MIN} min buffer).`
        );
        return;
      }
      if (portalInquiryOverlapsSibling(inv, dateStr, timeStr, null)) {
        setFormError('That time overlaps another of your requests. Choose a different slot.');
        return;
      }

      e.preventDefault();
      slotPickActiveRef.current = true;
      setHoverSlot(null);
      const initial = { dateKey: dateStr, timeStr, invalid: false };
      slotPickRef.current = initial;
      setSlotPick(initial);

      const onMove = (ev) => {
        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        const overEvent = el && el.closest ? el.closest('[data-portal-event="1"]') : null;
        const col = el && el.closest ? el.closest('[data-portal-day]') : null;
        if (!col) return;
        const ds = col.getAttribute('data-portal-day');
        if (!ds) return;
        const r = col.getBoundingClientRect();
        const yy = ev.clientY - r.top;
        if (yy < 0 || yy > totalSchedulePx) return;
        const ts = pxToTime(yy, WEEK_HOUR_PX);
        const blocked =
          !!overEvent ||
          isMinuteBlocked(ds, ts, blockedRangesByDateRef.current) ||
          portalInquiryOverlapsSibling(inquiriesRef.current, ds, ts, null);
        const next = { dateKey: ds, timeStr: ts, invalid: blocked };
        slotPickRef.current = next;
        setSlotPick(next);
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        slotPickActiveRef.current = false;
        const pick = slotPickRef.current;
        slotPickRef.current = null;
        setSlotPick(null);
        if (!pick) return;
        if (pick.invalid) {
          setFormError(
            `That time is unavailable — it overlaps a busy slot (includes ±${SCHEDULE_LEEWAY_MIN} min buffer).`
          );
          return;
        }
        setSelectedBooking({ dateKey: pick.dateKey, timeStr: pick.timeStr });
        setFormError(null);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [totalSchedulePx]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    const trimmed = issue.trim();
    if (!trimmed) {
      setFormError('Please describe the issue.');
      return;
    }
    if (!selectedBooking?.dateKey || !selectedBooking?.timeStr) {
      setFormError('Tap an empty time on the week grid below to choose your preferred visit time.');
      return;
    }
    if (isMinuteBlocked(selectedBooking.dateKey, selectedBooking.timeStr, blockedRangesByDate)) {
      setFormError(
        `That time is no longer available (busy slot or ±${SCHEDULE_LEEWAY_MIN} min buffer). Pick another time.`
      );
      return;
    }
    if (portalInquiryOverlapsSibling(inquiries, selectedBooking.dateKey, selectedBooking.timeStr, null)) {
      setFormError('That time overlaps another of your requests. Pick a different slot.');
      return;
    }
    if (!customer) return;

    setSubmitting(true);
    try {
      await submitServiceRequest({
        issue: trimmed,
        preferred_date: selectedBooking.dateKey,
        preferred_time: selectedBooking.timeStr
      });
      await refreshInquiries();
      await loadSchedule({ silent: true });
      setIssue('');
      setSelectedBooking(null);
      setJustSubmitted(true);
      window.setTimeout(() => setJustSubmitted(false), 4000);
    } catch (err) {
      setFormError(err.message || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const weekRangeLabel = useMemo(() => {
    const a = scheduleDates[0];
    const b = scheduleDates[6];
    if (!a || !b) return '';
    const opts = { day: 'numeric', month: 'short', year: 'numeric' };
    return `${a.toLocaleDateString('en-AU', opts)} – ${b.toLocaleDateString('en-AU', opts)}`;
  }, [scheduleDates]);

  const selectedBookingSummary = useMemo(() => {
    if (!selectedBooking?.dateKey) return null;
    const [y, m, d] = selectedBooking.dateKey.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    const dayPart = dt.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
    return `${dayPart} · ${formatPortalTime(selectedBooking.timeStr)}`;
  }, [selectedBooking]);

  const renderNoTimeEntry = (entry) => {
    if (entry.kind === 'inquiry') {
      const inq = entry.inquiry;
      const preview = inq.issue.length > 80 ? `${inq.issue.slice(0, 77)}…` : inq.issue;
      return (
        <div key={inq.id} style={styles.noTimeCardInq}>
          <span style={styles.noTimeBadgeInq}>Your request</span>
          <p style={styles.occupiedTitle}>Service request</p>
          <p style={styles.occupiedPreview}>{preview}</p>
        </div>
      );
    }
    const job = entry.job;
    const yours = job.is_yours !== false;
    if (!yours) {
      return (
        <div key={`job-${job.id}`} style={styles.noTimeCardBlocked}>
          <span style={styles.noTimeBadgeBlocked}>Unavailable</span>
          <p style={styles.occupiedTitleWhite}>Busy · time TBC</p>
        </div>
      );
    }
    return (
      <div key={`job-${job.id}`} style={styles.noTimeCardJob}>
        <span style={styles.noTimeBadgeJob}>Job</span>
        <p style={styles.occupiedTitleWhite}>{job.job_type}</p>
        {job.address && <p style={styles.occupiedMetaLight}>📍 {job.address}</p>}
      </div>
    );
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.diagonalBackground}>
        <div style={styles.diagonalBar} />
      </div>

      <nav style={styles.navbar}>
        <div style={styles.navContent}>
          <div style={styles.navLeft}>
            <div style={styles.navLogo}>🔧</div>
            <h1 style={styles.navTitle}>Customer Portal</h1>
          </div>
          <button type="button" onClick={handleLogout} style={styles.logoutButton}>
            Logout
          </button>
        </div>
      </nav>

      <main style={styles.main}>
        <div style={styles.content}>
          <div style={styles.breadcrumb}>
            <Link to="/portal/dashboard" style={styles.breadcrumbLink}>Dashboard</Link>
            <span style={styles.breadcrumbSeparator}>/</span>
            <span style={styles.breadcrumbCurrent}>Request service</span>
          </div>

          <div style={styles.headerRow}>
            <div>
              <h2 style={styles.pageTitle}>Request service</h2>
              <p style={styles.pageSubtitle}>
                Tap a free slot in the week grid. Your jobs are blue; other customers show as blocked (grey). Bookings use ±{SCHEDULE_LEEWAY_MIN} min buffer; back-to-back jobs within {SCHEDULE_MERGE_GAP_MIN} min show as one block.
              </p>
            </div>
          </div>

          <div style={styles.formSection}>
            <h3 style={styles.sectionHeading}>What is the issue?</h3>
            {justSubmitted && (
              <div style={styles.successBanner}>
                Your request was placed in the schedule.
              </div>
            )}
            {formError && (
              <div style={styles.errorBox}>
                <span>⚠️</span> {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} style={styles.form}>
              <label style={styles.label}>
                Issue details
                <textarea
                  value={issue}
                  onChange={(e) => setIssue(e.target.value)}
                  placeholder="Describe the problem or work you need..."
                  rows={4}
                  style={styles.textarea}
                />
              </label>

            <div style={styles.scheduleInFormWrap}>
              <div style={styles.sectionHeaderSchedule}>
                <h3 style={styles.sectionTitle}>📅 Planned schedule</h3>
                <span style={styles.sectionHint}>
                  {fmtHour(WEEK_START_H)}–{fmtHour(WEEK_END_H - 1)} · week from today
                </span>
              </div>

              <div style={styles.weekToolbar}>
                <button
                  type="button"
                  aria-label="Previous week"
                  style={styles.dayNavBtn}
                  onClick={() => {
                    setWeekOffset((w) => w - 1);
                    setSelectedBooking(null);
                  }}
                >
                  ←
                </button>
                <div style={styles.weekRangeWrap}>
                  <span style={styles.weekRangeText}>{weekRangeLabel}</span>
                  {weekOffset !== 0 && (
                    <button
                      type="button"
                      style={styles.thisWeekBtn}
                      onClick={() => {
                        setWeekOffset(0);
                        setSelectedBooking(null);
                      }}
                    >
                      This week
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Next week"
                  style={styles.dayNavBtn}
                  onClick={() => {
                    setWeekOffset((w) => w + 1);
                    setSelectedBooking(null);
                  }}
                >
                  →
                </button>
              </div>

              {loading ? (
                <div style={styles.loadingState}>
                  <div style={styles.spinner} />
                  <p>Loading schedule…</p>
                </div>
              ) : (
                <>
                  <div style={styles.weekGridScroll}>
                    <div style={styles.weekGridOuter}>
                      <div style={styles.weekGridHeader}>
                        <div style={styles.weekCorner} />
                        {scheduleDates.map((d, i) => {
                          const dStr = toDateStr(d);
                          const isToday = dStr === todayStr;
                          const label = isToday ? 'Today' : d.toLocaleDateString('en-AU', { weekday: 'short' });
                          return (
                            <div
                              key={dStr}
                              style={{
                                ...styles.weekHeadCell,
                                borderRight: i < scheduleDates.length - 1 ? '1px solid rgba(229, 231, 235, 0.95)' : 'none'
                              }}
                            >
                              <div style={styles.weekHeadLabel}>{label}</div>
                              <div
                                style={{
                                  ...styles.weekHeadDateCircle,
                                  background: isToday ? '#3b82f6' : 'transparent',
                                  color: isToday ? '#fff' : '#111827'
                                }}
                              >
                                {d.getDate()}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div style={styles.weekGridBody}>
                        <div style={{ ...styles.weekTimeCol, height: `${totalSchedulePx}px` }}>
                          {WEEK_SCHED_HRS.map((h, i) => (
                            <div
                              key={h}
                              style={{
                                ...styles.weekTimeTick,
                                top: `${i * WEEK_HOUR_PX}px`,
                                borderTop: i > 0 ? '1px solid rgba(229, 231, 235, 0.9)' : 'none'
                              }}
                            >
                              <span style={styles.weekTimeTickLabel}>{fmtHour(h)}</span>
                            </div>
                          ))}
                        </div>

                        {scheduleDates.map((d, dayIdx) => {
                          const dateStr = toDateStr(d);
                          const dayEvents = byDate[dateStr] || [];
                          const isTodayCol = dateStr === todayStr;
                          return (
                            <div
                              key={dateStr}
                              data-portal-day={dateStr}
                              role="presentation"
                              onPointerDown={(e) => handleColumnPointerDown(dateStr, e)}
                              onMouseMove={(e) => handleColumnMouseMove(dateStr, e)}
                              onMouseLeave={handleColumnMouseLeave}
                              style={{
                                ...styles.weekDayCol,
                                height: `${totalSchedulePx}px`,
                                borderRight: dayIdx < scheduleDates.length - 1 ? '1px solid rgba(229, 231, 235, 0.85)' : 'none',
                                cursor: 'crosshair',
                                ...(dragGhost?.dropPreview?.dateStr === dateStr
                                  ? {
                                      background: dragGhost.dropPreview.invalid
                                        ? 'rgba(254, 226, 226, 0.4)'
                                        : 'rgba(219, 234, 254, 0.5)'
                                    }
                                  : {})
                              }}
                            >
                              {WEEK_SCHED_HRS.map((_, i) => (
                                <React.Fragment key={i}>
                                  {i > 0 && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        top: `${i * WEEK_HOUR_PX}px`,
                                        left: 0,
                                        right: 0,
                                        borderTop: '1px solid rgba(229, 231, 235, 0.85)',
                                        pointerEvents: 'none'
                                      }}
                                    />
                                  )}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: `${i * WEEK_HOUR_PX + WEEK_HOUR_PX / 2}px`,
                                      left: 0,
                                      right: 0,
                                      borderTop: '1px dashed rgba(229, 231, 235, 0.65)',
                                      pointerEvents: 'none'
                                    }}
                                  />
                                </React.Fragment>
                              ))}

                              {isTodayCol &&
                                (() => {
                                  const h = nowTick.getHours();
                                  if (h >= WEEK_END_H || h < WEEK_START_H) return null;
                                  return (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        top: `${timeToPx(
                                          `${String(nowTick.getHours()).padStart(2, '0')}:${String(nowTick.getMinutes()).padStart(2, '0')}`,
                                          WEEK_HOUR_PX
                                        )}px`,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: '#10b981',
                                        zIndex: 12,
                                        pointerEvents: 'none',
                                        boxShadow: '0 0 8px rgba(16,185,129,0.6)'
                                      }}
                                    />
                                  );
                                })()}

                              {hoverSlot &&
                                hoverSlot.dateKey === dateStr &&
                                !slotPick &&
                                !dragGhost && (
                                  <div
                                    aria-hidden
                                    style={{
                                      position: 'absolute',
                                      top: `${timeToPx(hoverSlot.timeStr, WEEK_HOUR_PX) + 1}px`,
                                      left: 1,
                                      right: 1,
                                      height: `${slotBoxHeightPx}px`,
                                      border: '1px dashed #f97316',
                                      borderRadius: '8px',
                                      background: 'rgba(249, 115, 22, 0.05)',
                                      opacity: 0.65,
                                      pointerEvents: 'none',
                                      zIndex: 7,
                                      transition: 'opacity 0.08s ease'
                                    }}
                                  />
                                )}

                              {selectedBooking?.dateKey === dateStr && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: `${timeToPx(selectedBooking.timeStr, WEEK_HOUR_PX) + 1}px`,
                                    left: 1,
                                    right: 1,
                                    height: `${slotBoxHeightPx}px`,
                                    border: '1px dashed #f97316',
                                    borderRadius: '8px',
                                    background: 'rgba(249, 115, 22, 0.06)',
                                    pointerEvents: 'none',
                                    zIndex: 8
                                  }}
                                />
                              )}

                              {slotPick && slotPick.dateKey === dateStr && (
                                <div
                                  aria-hidden
                                  style={{
                                    position: 'absolute',
                                    top: `${timeToPx(slotPick.timeStr, WEEK_HOUR_PX) + 1}px`,
                                    left: 1,
                                    right: 1,
                                    height: `${slotBoxHeightPx}px`,
                                    border: `1px dashed ${slotPick.invalid ? '#ef4444' : '#ea580c'}`,
                                    borderRadius: '8px',
                                    background: slotPick.invalid
                                      ? 'rgba(254, 226, 226, 0.35)'
                                      : 'rgba(249, 115, 22, 0.14)',
                                    pointerEvents: 'none',
                                    zIndex: 9,
                                    boxShadow: slotPick.invalid ? 'none' : '0 0 0 1px rgba(249,115,22,0.25)'
                                  }}
                                />
                              )}

                              {dayEvents.map((seg) => {
                                const common = {
                                  position: 'absolute',
                                  top: seg.top,
                                  left: 3,
                                  right: 3,
                                  height: seg.height,
                                  borderRadius: '6px',
                                  padding: '4px 6px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  zIndex: 10,
                                  overflow: 'hidden',
                                  cursor: 'default',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                                };
                                if (seg.variant === 'blocked') {
                                  return (
                                    <div
                                      key={seg.key}
                                      data-portal-event="1"
                                      data-portal-busy="1"
                                      onPointerDown={(e) => e.stopPropagation()}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        ...common,
                                        background: '#ef4444',
                                        color: '#fff7ed',
                                        border: '1px solid #b91c1c',
                                        boxShadow: '0 2px 8px rgba(220, 38, 38, 0.35)'
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: '9px',
                                          fontWeight: 'bold',
                                          textTransform: 'uppercase',
                                          marginBottom: '2px',
                                          letterSpacing: '0.06em',
                                          color: 'rgba(255, 255, 255, 0.95)'
                                        }}
                                      >
                                        Unavailable
                                      </div>
                                      <div
                                        style={{
                                          fontWeight: 700,
                                          fontSize: '11px',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          color: '#fff'
                                        }}
                                      >
                                        {seg.title}
                                      </div>
                                      {seg.subtitle && seg.height > 36 && (
                                        <div style={{ fontSize: '9px', opacity: 0.92, marginTop: '2px', color: '#fecaca' }}>
                                          {seg.subtitle}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                if (seg.variant === 'yours_inquiry') {
                                  const canDrag = !!seg.draggableInquiryId;
                                  const locked = !!seg.inquiryLocked;
                                  const isDraggingThis =
                                    canDrag && dragGhost && dragGhost.inquiryId === seg.draggableInquiryId;
                                  const acceptedStyle = locked
                                    ? {
                                        background: 'linear-gradient(180deg, #60a5fa 0%, #2563eb 100%)',
                                        boxShadow: '0 2px 6px rgba(37, 99, 235, 0.35)',
                                        border: '1px solid rgba(29, 78, 216, 0.5)'
                                      }
                                    : {
                                        background: 'linear-gradient(180deg, #fbbf24 0%, #d97706 100%)',
                                        boxShadow: '0 2px 6px rgba(217, 119, 6, 0.35)',
                                        border: 'none'
                                      };
                                  return (
                                    <div
                                      key={seg.key}
                                      data-portal-event="1"
                                      data-draggable-inquiry={canDrag ? '1' : undefined}
                                      onClick={(e) => e.stopPropagation()}
                                      onPointerDown={(e) => {
                                        e.stopPropagation();
                                        if (canDrag) handleInquiryDragStart(e, seg);
                                      }}
                                      style={{
                                        ...common,
                                        padding: '2px 6px 3px',
                                        ...acceptedStyle,
                                        color: '#fff',
                                        cursor: canDrag ? 'grab' : 'default',
                                        opacity: isDraggingThis ? 0.35 : 1,
                                        touchAction: canDrag ? 'none' : undefined
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: '8px',
                                          fontWeight: 'bold',
                                          textTransform: 'uppercase',
                                          lineHeight: 1.15,
                                          marginBottom: '1px',
                                          color: 'rgba(255,255,255,0.95)',
                                          letterSpacing: locked ? '0.06em' : undefined
                                        }}
                                      >
                                        {locked
                                          ? 'Inquiry'
                                          : canDrag
                                            ? 'Your request · drag to move'
                                            : 'Your request'}
                                      </div>
                                      <div
                                        style={{
                                          fontWeight: 700,
                                          fontSize: '10px',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }}
                                      >
                                        {seg.title}
                                      </div>
                                    </div>
                                  );
                                }
                                const headLine = seg.variant === 'yours_mixed' ? 'YOUR BOOKINGS' : 'YOUR JOB';
                                return (
                                  <div
                                    key={seg.key}
                                    data-portal-event="1"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      ...common,
                                      background: seg.variant === 'yours_mixed' ? '#2563eb' : '#3b82f6',
                                      color: '#fff',
                                      boxShadow: '0 2px 6px rgba(59, 130, 246, 0.35)'
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '9px',
                                        fontWeight: 'bold',
                                        color: 'rgba(255,255,255,0.92)',
                                        textTransform: 'uppercase',
                                        marginBottom: '2px',
                                        letterSpacing: '0.02em'
                                      }}
                                    >
                                      {headLine}
                                    </div>
                                    <div
                                      style={{
                                        fontWeight: 700,
                                        fontSize: '11px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }}
                                    >
                                      {seg.title}
                                    </div>
                                    {seg.subtitle && seg.height > 38 && (
                                      <div style={{ fontSize: '9px', opacity: 0.92, marginTop: '2px' }}>{seg.subtitle}</div>
                                    )}
                                  </div>
                                );
                              })}
                              {dragGhost?.dropPreview?.dateStr === dateStr && (
                                <>
                                  <div
                                    aria-hidden
                                    style={{
                                      position: 'absolute',
                                      left: 3,
                                      right: 3,
                                      top: dragGhost.dropPreview.topPx,
                                      height: dragGhost.dropPreview.heightPx,
                                      border: `2px dashed ${dragGhost.dropPreview.invalid ? '#ef4444' : '#3b82f6'}`,
                                      background: dragGhost.dropPreview.invalid
                                        ? 'rgba(239, 68, 68, 0.12)'
                                        : 'rgba(59, 130, 246, 0.16)',
                                      borderRadius: '6px',
                                      pointerEvents: 'none',
                                      zIndex: 25,
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                  <div
                                    aria-hidden
                                    style={{
                                      position: 'absolute',
                                      left: 0,
                                      right: 0,
                                      top: dragGhost.dropPreview.topPx,
                                      height: '2px',
                                      background: dragGhost.dropPreview.invalid ? '#ef4444' : '#3b82f6',
                                      pointerEvents: 'none',
                                      zIndex: 26,
                                      boxShadow: dragGhost.dropPreview.invalid
                                        ? '0 0 6px rgba(239,68,68,0.5)'
                                        : '0 0 6px rgba(59,130,246,0.45)'
                                    }}
                                  />
                                  <div
                                    aria-hidden
                                    style={{
                                      position: 'absolute',
                                      left: 0,
                                      right: 0,
                                      top: dragGhost.dropPreview.topPx + dragGhost.dropPreview.heightPx,
                                      height: '2px',
                                      background: dragGhost.dropPreview.invalid ? '#ef4444' : '#3b82f6',
                                      pointerEvents: 'none',
                                      zIndex: 26,
                                      boxShadow: dragGhost.dropPreview.invalid
                                        ? '0 0 6px rgba(239,68,68,0.5)'
                                        : '0 0 6px rgba(59,130,246,0.45)'
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {noTime.length > 0 && (
                    <div style={styles.noTimeBelowWeek}>
                      <p style={styles.noTimeBelowTitle}>Time to be confirmed</p>
                      <div style={styles.noTimeBelowInner}>
                        {noTime.map((entry) => renderNoTimeEntry(entry))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

              <button
                type="submit"
                disabled={submitting}
                style={{
                  ...styles.submitButton,
                  opacity: submitting ? 0.7 : 1
                }}
              >
                {submitting ? 'Submitting…' : 'Submit request'}
              </button>
              {selectedBookingSummary && (
                <p style={styles.selectedSlotHint}>
                  Selected visit: <strong>{selectedBookingSummary}</strong>
                </p>
              )}
            </form>
          </div>

          <Link to="/portal/dashboard" style={styles.backLink}>
            ← Back to Dashboard
          </Link>
        </div>
      </main>

      {dragGhost && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            left: dragGhost.x,
            top: dragGhost.y,
            transform: 'translate(-50%, -50%)',
            zIndex: 10000,
            pointerEvents: 'none',
            maxWidth: '200px',
            padding: '8px 10px',
            borderRadius: '8px',
            background: 'linear-gradient(180deg, #fbbf24 0%, #d97706 100%)',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 700,
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            border: '2px solid rgba(255,255,255,0.9)'
          }}
        >
          <div style={{ fontSize: '9px', textTransform: 'uppercase', opacity: 0.95, marginBottom: '4px' }}>Moving</div>
          <div style={{ lineHeight: 1.3 }}>{dragGhost.title}</div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    width: '100%',
    maxWidth: '100%',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxSizing: 'border-box'
  },
  diagonalBackground: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    pointerEvents: 'none',
    overflow: 'hidden'
  },
  diagonalBar: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '340%',
    height: '260px',
    transform: 'translate(-50%, -50%) rotate(-12deg)',
    transformOrigin: 'center',
    background: 'linear-gradient(90deg, rgba(220,220,225,0) 0%, rgba(220,220,225,0.14) 8%, rgba(200,200,210,0.42) 18%, rgba(140,145,160,0.32) 30%, rgba(140,145,160,0.32) 70%, rgba(200,200,210,0.42) 82%, rgba(220,220,225,0.14) 92%, rgba(220,220,225,0) 100%)',
    filter: 'blur(80px)',
    overflow: 'hidden'
  },
  navbar: {
    background: 'rgba(255, 255, 255, 0.8)',
    borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    width: '100%',
    backdropFilter: 'blur(10px)'
  },
  navContent: {
    maxWidth: '100%',
    margin: '0 auto',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  navLogo: { fontSize: '24px' },
  navTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  logoutButton: {
    background: 'transparent',
    border: '1px solid #d1d5db',
    color: '#6b7280',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  main: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    minHeight: 'calc(100vh - 73px)',
    padding: '16px 24px 32px',
    boxSizing: 'border-box',
    width: '100%',
    position: 'relative',
    zIndex: 1
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    maxWidth: '100%',
    flex: '1 1 auto',
    minWidth: 0
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#6b7280'
  },
  breadcrumbLink: {
    color: '#f97316',
    textDecoration: 'none',
    fontWeight: 500
  },
  breadcrumbSeparator: { color: '#d1d5db' },
  breadcrumbCurrent: { color: '#374151' },
  headerRow: { marginBottom: '4px' },
  pageTitle: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 8px 0'
  },
  pageSubtitle: {
    fontSize: '15px',
    color: '#6b7280',
    margin: 0,
    lineHeight: 1.5,
    maxWidth: 'none'
  },
  formSection: {
    background: 'rgba(255, 255, 255, 0.85)',
    borderRadius: '12px',
    padding: '20px 24px',
    border: '1px solid rgba(229, 231, 235, 0.9)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch'
  },
  scheduleInFormWrap: {
    alignSelf: 'stretch',
    width: '100%',
    maxWidth: '100%',
    marginBottom: '20px',
    border: '1px solid rgba(229, 231, 235, 0.95)',
    borderRadius: '12px',
    overflow: 'hidden',
    background: 'rgba(249, 250, 251, 0.45)'
  },
  sectionHeaderSchedule: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 14px',
    borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
    background: 'rgba(255, 255, 255, 0.65)',
    flexWrap: 'wrap',
    gap: '8px'
  },
  sectionHeading: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
    margin: '0 0 16px 0'
  },
  successBanner: {
    background: 'rgba(220, 252, 231, 0.9)',
    border: '1px solid rgba(134, 239, 172, 0.8)',
    color: '#166534',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '14px',
    marginBottom: '12px'
  },
  errorBox: {
    background: 'rgba(254, 242, 242, 0.9)',
    border: '1px solid rgba(254, 202, 202, 0.8)',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#dc2626',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151'
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '15px',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '100px',
    boxSizing: 'border-box'
  },
  submitButton: {
    alignSelf: 'flex-start',
    background: '#f97316',
    color: '#fff',
    border: 'none',
    padding: '12px 22px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  selectedSlotHint: {
    margin: 0,
    fontSize: '14px',
    color: '#374151'
  },
  weekToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
    flexWrap: 'wrap',
    background: 'rgba(249, 250, 251, 0.9)'
  },
  weekRangeWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    minWidth: '200px',
    flex: '1 1 220px'
  },
  weekRangeText: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
    textAlign: 'center'
  },
  thisWeekBtn: {
    border: 'none',
    background: 'transparent',
    color: '#f97316',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  weekGridScroll: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    width: '100%',
    maxWidth: '100%'
  },
  weekGridOuter: {
    minWidth: '460px',
    border: '1px solid rgba(229, 231, 235, 0.95)',
    borderRadius: '0 0 12px 12px',
    background: 'rgba(255, 255, 255, 0.95)'
  },
  weekGridHeader: {
    display: 'grid',
    gridTemplateColumns: '34px repeat(7, minmax(42px, 1fr))',
    borderBottom: '1px solid rgba(229, 231, 235, 0.95)',
    background: 'rgba(249, 250, 251, 0.98)'
  },
  weekCorner: {
    padding: '8px',
    borderRight: '1px solid rgba(229, 231, 235, 0.95)'
  },
  weekHeadCell: {
    padding: '6px 4px',
    textAlign: 'center'
  },
  weekHeadLabel: {
    fontSize: '9px',
    fontWeight: 700,
    color: '#6b7280',
    letterSpacing: '0.02em'
  },
  weekHeadDateCircle: {
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '4px auto 0',
    fontWeight: 'bold',
    fontSize: '12px'
  },
  weekGridBody: {
    display: 'grid',
    gridTemplateColumns: '34px repeat(7, minmax(42px, 1fr))'
  },
  weekTimeCol: {
    position: 'relative',
    background: 'rgba(249, 250, 251, 0.98)',
    borderRight: '1px solid rgba(229, 231, 235, 0.95)'
  },
  weekTimeTick: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: `${WEEK_HOUR_PX}px`,
    padding: '2px 4px',
    boxSizing: 'border-box'
  },
  weekTimeTickLabel: {
    fontSize: '9px',
    fontWeight: 'bold',
    color: '#6b7280'
  },
  weekDayCol: {
    position: 'relative',
    background: 'transparent',
    boxSizing: 'border-box'
  },
  noTimeBelowWeek: {
    padding: '16px 20px',
    borderTop: '1px solid rgba(229, 231, 235, 0.85)',
    background: 'rgba(254, 252, 232, 0.45)'
  },
  noTimeBelowTitle: {
    margin: '0 0 10px 0',
    fontSize: '13px',
    fontWeight: 600,
    color: '#92400e'
  },
  noTimeBelowInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  noTimeCardJob: {
    background: '#3b82f6',
    borderRadius: '10px',
    padding: '10px 12px',
    color: '#fff'
  },
  noTimeCardBlocked: {
    background: '#ef4444',
    borderRadius: '10px',
    padding: '10px 12px',
    color: '#fff7ed',
    border: '1px solid #b91c1c',
    boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)'
  },
  noTimeBadgeBlocked: {
    display: 'inline-block',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'rgba(255,255,255,0.95)',
    marginBottom: '6px'
  },
  noTimeCardInq: {
    background: 'rgba(255, 255, 255, 0.95)',
    border: '1px solid rgba(251, 191, 36, 0.6)',
    borderRadius: '10px',
    padding: '10px 12px'
  },
  noTimeBadgeJob: {
    display: 'inline-block',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'rgba(255,255,255,0.9)',
    marginBottom: '6px'
  },
  noTimeBadgeInq: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#d97706',
    marginBottom: '6px'
  },
  occupiedTitleWhite: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    margin: '0 0 4px 0'
  },
  occupiedMetaLight: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.88)',
    margin: 0
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
    margin: 0
  },
  sectionHint: {
    fontSize: '13px',
    color: '#9ca3af'
  },
  dayToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
    flexWrap: 'wrap',
    background: 'rgba(249, 250, 251, 0.9)'
  },
  dayNavBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    background: '#fff',
    fontSize: '18px',
    cursor: 'pointer',
    color: '#374151'
  },
  dayPickerWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    minWidth: '200px'
  },
  dateInput: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '15px',
    fontFamily: 'inherit'
  },
  dayLongLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827'
  },
  loadingState: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280'
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#f97316',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 16px'
  },
  dayGrid: {
    padding: '0'
  },
  slotRow: {
    display: 'flex',
    borderBottom: '1px solid rgba(229, 231, 235, 0.9)',
    minHeight: '72px',
    alignItems: 'stretch'
  },
  timeCol: {
    width: '132px',
    flexShrink: 0,
    padding: '12px 16px',
    background: 'rgba(249, 250, 251, 0.95)',
    borderRight: '1px solid rgba(229, 231, 235, 0.9)',
    display: 'flex',
    alignItems: 'center'
  },
  timeColLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#4b5563',
    lineHeight: 1.35
  },
  timeColMuted: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#9ca3af'
  },
  slotsCol: {
    flex: 1,
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0
  },
  emptySlot: {
    width: '100%',
    minHeight: '52px',
    borderRadius: '10px',
    border: '2px dashed #d1d5db',
    background: 'rgba(249, 250, 251, 0.6)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 14px',
    transition: 'border-color 0.15s, background 0.15s'
  },
  emptySlotSelected: {
    border: '2px solid #f97316',
    background: 'rgba(249, 115, 22, 0.08)'
  },
  emptySlotText: {
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: 500,
    textAlign: 'center',
    lineHeight: 1.4
  },
  occupiedWrap: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  occupiedInner: {
    background: 'rgba(255, 255, 255, 0.95)',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '10px 12px'
  },
  occupiedBadgeInq: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#d97706',
    marginBottom: '6px'
  },
  occupiedBadgeJob: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '999px',
    marginBottom: '6px'
  },
  occupiedTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
    margin: '0 0 4px 0'
  },
  occupiedPreview: {
    fontSize: '13px',
    color: '#4b5563',
    margin: 0,
    lineHeight: 1.4
  },
  occupiedMeta: {
    fontSize: '12px',
    color: '#6b7280',
    margin: 0
  },
  noTimeRow: {
    display: 'flex',
    borderBottom: '1px solid rgba(229, 231, 235, 0.9)',
    alignItems: 'stretch',
    background: 'rgba(254, 252, 232, 0.35)'
  },
  noTimeBox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  backLink: {
    color: '#f97316',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
    marginTop: '8px'
  }
};

export default PortalServiceRequest;
